import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {string | undefined} The server-rendered "decrease quantity" label, captured before it is swapped. */
  #decreaseLabel;

  /**
   * Whether the minus button doubles as a remove control at the minimum
   * quantity. True for cart lines; false for the quick order list, which shares
   * this component but has no line to remove (its minimum is 0).
   * @returns {boolean}
   */
  get #removesAtMin() {
    return this.hasAttribute('data-remove-at-min');
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // On a cart line the minus button becomes a REMOVE button at the minimum
    // rather than a disabled one (see decreaseQuantity below), so it stays
    // clickable. The attribute is what swaps the "−" glyph for the trash icon.
    const atMin = value <= min;
    if (this.#removesAtMin) {
      this.toggleAttribute('data-at-min', atMin);
      minusButton.disabled = false;
      this.#syncMinusLabel(atMin);
    } else {
      minusButton.disabled = atMin;
    }

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Keeps the screen-reader label on the minus button honest about what a click
   * will actually do. Both labels are lifted from markup the theme already
   * localises, so nothing new needs translating.
   * @param {boolean} atMin - Whether a click would remove the line
   */
  #syncMinusLabel(atMin) {
    const label = this.refs.minusButton.querySelector('.visually-hidden');
    if (!label) return;

    this.#decreaseLabel ??= label.textContent ?? '';
    const removeLabel = this.closest('.cart-items__table-row')
      ?.querySelector('.cart-items__remove')
      ?.getAttribute('aria-label');

    label.textContent = atMin ? removeLabel || 'Remove' : this.#decreaseLabel;
  }

  /**
   * Decreasing past the minimum removes the line instead of doing nothing.
   * Delegates to the row's existing remove button so the removal takes the same
   * path (optimistic UI, section re-render, cart events) as the X control.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    const { min, value } = this.getCurrentValues();

    if (!this.#removesAtMin || value > min) {
      super.decreaseQuantity(event);
      return;
    }

    event.preventDefault();
    const removeButton = this.closest('.cart-items__table-row')?.querySelector('.cart-items__remove');
    if (removeButton instanceof HTMLElement) removeButton.click();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
